/**
 * Request Naming Utility
 * Generates standardized request names for API calls that appear in Chrome DevTools Network tab
 * 
 * Pattern: b-esper_{type}_{method}_{description}
 * - type: 'pp' (per-page) or 'ah' (ad-hoc)
 * - method: HTTP method (get, post, put, delete, patch)
 * - description: Short description (e.g., "listtenants", "checkuseragreements")
 */

/**
 * Generate a standardized request name for API calls
 * @param {string} type - 'pp' (per-page) or 'ah' (ad-hoc), defaults to 'ah'
 * @param {string} method - HTTP method (get, post, put, delete, patch)
 * @param {string} description - Short description (e.g., "listtenants", "checkuseragreements")
 * @returns {string} Request name in format: b-esper_{type}_{method}_{description}
 */
export function generateRequestName(type = 'ah', method = 'get', description = '') {
  // Normalize method to lowercase
  const normalizedMethod = method.toLowerCase();
  
  // Normalize type to lowercase
  const normalizedType = type.toLowerCase();
  
  // Ensure type is either 'pp' or 'ah'
  const requestType = (normalizedType === 'pp') ? 'pp' : 'ah';
  
  // Normalize description: lowercase, remove spaces, keep only alphanumeric and underscores
  const normalizedDescription = description
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9_]/g, '');
  
  if (!normalizedDescription) {
    console.warn('Request name description is empty, using "unknown"');
    return `b-esper_${requestType}_${normalizedMethod}_unknown`;
  }
  
  return `b-esper_${requestType}_${normalizedMethod}_${normalizedDescription}`;
}

/**
 * Helper to generate per-page request names
 * @param {string} method - HTTP method
 * @param {string} description - Short description
 * @returns {string} Request name with 'pp' type
 */
export function generatePerPageRequestName(method, description) {
  return generateRequestName('pp', method, description);
}

/**
 * Helper to generate ad-hoc request names
 * @param {string} method - HTTP method
 * @param {string} description - Short description
 * @returns {string} Request name with 'ah' type
 */
export function generateAdHocRequestName(method, description) {
  return generateRequestName('ah', method, description);
}

